import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { Col, Input, List, Modal, Row, Spin } from "antd";
import { useFormik } from "formik";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Button from "../../../components/functional_components/Button/PFButton";
import { setLoading } from "../../../redux/ducks/Loading";
import ProjectEstimateStatus from "../../../services/api/ProjectEstimateStatus";
import { paginationDefaults } from "../../../utils/properties";
import { showError, showSuccess, trimData } from "../../../utils/utilities";

const { confirm } = Modal;

const Status = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    loading: false,
    modal: {
      visible: false,
      type: "",
      data: {},
    },
  });

  useEffect(() => {
    dataList();
  }, []);

  const formikStatus = useFormik({
    initialValues: {
      id: get(state.modal.data, "id", ""),
      name: get(state.modal.data, "name", ""),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.id) {
        errors.id = "Required!";
      }
      if (!values.name) {
        errors.name = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const saveResponse = await ProjectEstimateStatus.saveStatus(values);
      if (!saveResponse.error) {
        showSuccess();
        closeModal();
        dataList();
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
        return saveResponse.error;
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const dataList = async (page = state.pagination.currentPage) => {
    dispatch(setLoading(true));
    const statuses = await ProjectEstimateStatus.getStatusesList(`page=${page - 1}&size=${state.pagination.size}`);
    if (!statuses.error && statuses.data) {
      statuses.data.forEach((status, i) => {
        status.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: get(statuses, "data", []),
        pagination: {
          currentPage: statuses.currentPage ? statuses.currentPage + 1 : paginationDefaults.page,
          size: get(statuses, "pageSize", paginationDefaults.size),
          totalItems: get(statuses, "totalItems", paginationDefaults.totalItems),
        },
      }));
    } else {
      showError(get(statuses, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const statusData = await ProjectEstimateStatus.statusData(id);
    if (!statusData.error && statusData.data) {
      setState((prevState) => ({ ...prevState, modal: { visible: true, type: "edit", data: statusData.data } }));
    } else {
      showError(get(statusData, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteCallback = async (statusId) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await ProjectEstimateStatus.deleteStatus(statusId);
        if (!deleteResponse.error) {
          showSuccess();
          dataList();
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const closeModal = () => {
    formikStatus.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
  };

  return (
    <>
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
        />
      </Row>
      <List
        header={<strong>{t("menuLeft.status")}</strong>}
        bordered
        size="small"
        pagination={{
          size: "small",
          showSizeChanger: false,
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                size: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(page);
          },
        }}
        dataSource={state.tableContent}
        renderItem={(item) => (
          <List.Item
            actions={[
              <Row className="list-actions" key={item.id}>
                <Col key={item.id} onClick={() => editCallback(item.id)}>
                  <EditOutlined className="icon-medium mr-10" />
                </Col>
                {
                  <Col onClick={item.deletable ? () => deleteCallback(item.id) : undefined}>
                    <DeleteOutlined className={`icon-medium ${!item.deletable ? "disabled no-pointer" : ""}`} />
                  </Col>
                }
              </Row>,
            ]}
          >
            <List.Item.Meta title={<div>{`${item.id} | ${item.name}`}</div>} />
          </List.Item>
        )}
      />

      <Modal
        open={state.modal.visible}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.id ? state.modal.data.id : t("status.new")
        }
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikStatus.submitForm}
            isDisabled={!(formikStatus.dirty && formikStatus.isValid)}
          />,
        ]}
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="id">
                {t("costCenters.table.id")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="id"
                name="id"
                onChange={formikStatus.handleChange}
                onBlur={formikStatus.handleBlur}
                value={formikStatus.values.id}
                disabled={state.modal.type === "edit" ? true : false}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="name">
                {t("costCenters.table.name")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="name"
                name="name"
                onChange={formikStatus.handleChange}
                onBlur={formikStatus.handleBlur}
                value={formikStatus.values.name}
              />
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default Status;
